// SetReceiveAmount.tsx
import { observer } from "mobx-react";
import React, { useState } from "react";
import {
  Modal,
  Stack,
  TextInput,
  Paper,
  Group,
  Button,
  Text,
} from "@mantine/core";

import { MODAL_WIDTH_FOR_DATA_INTENSIVE_MODALS } from "../../../helpers/constants";
import { BOB_MODALS } from "../../helpers/constants";
import { Bob } from "../../stores/Bob";
import UserCreatedInvoiceQR from "./helpers/UserCreatedInvoiceQR";
import { NumericKeypad } from "../details/numericKeypad";
import { formatFiat } from "../../helpers/formatNumbers";

const SetReceiveAmount = observer(({ bob }: { bob: Bob }) => {
  const [amount, setAmount] = useState<number | "">("");
  const [description, setDescription] = useState("");

  // Add validation logic
  const isAmountTooHigh =
    typeof amount === "number" && amount > bob.workspace.maxInvoiceSatoshis;
  const isValidAmount =
    typeof amount === "number" && amount > 0 && !isAmountTooHigh;

  const handleMakeInvoice = () => {
    if (typeof amount !== "number" || amount < 0) return;
    bob.createInvoice(amount, description.trim());
  };

  const handleChangeAmount = (value: number | "") => {
    if (bob.userCreatedInvoicePayRequest)
      bob.setUserCreatedInvoicePayRequest(null);
    setAmount(value);
  };

  const handleClose = () => {
    bob.setOpenModal(null);
    bob.setUserCreatedInvoicePayRequest(null);
  };

  const isInvoiceReady = !!bob.userCreatedInvoicePayRequest;

  return (
    <Modal
      opened={bob.openModal === BOB_MODALS.SET_RECEIVE_AMOUNT}
      onClose={handleClose}
      title={isInvoiceReady ? "Invoice created" : "Set Invoice Amount"}
    >
      <Paper withBorder shadow="md" p={24} radius="md">
        <Stack>
          {!isInvoiceReady && (
            <NumericKeypad
              currencyCode={bob.currencyCode}
              currencySnapshot={bob.workspace.exchangeRates.data}
              initialMode="sats"
              valueSats={typeof amount === "number" ? amount : 0}
              minSats={0}
              maxSats={bob.workspace.maxInvoiceSatoshis}
              error={
                isAmountTooHigh
                  ? `maximum ${bob.workspace.maxInvoiceSatoshis.toLocaleString()} satoshis`
                  : undefined
              }
              onChangeSats={handleChangeAmount}
            />
          )}

          {!isInvoiceReady && (
            <TextInput
              label="Invoice comments"
              placeholder="Optional note (max 100 chars)"
              description="Appears in the memo/description field of the invoice."
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              maxLength={100}
            />
          )}

          {!isInvoiceReady ? (
            /* buttons shown before invoice creation */
            <Group justify="flex-end" mt="md">
              <Button variant="outline" onClick={handleClose}>
                Exit
              </Button>
              <Button
                onClick={handleMakeInvoice}
                disabled={bob.areWeWaitingOnRails || !isValidAmount}
                loading={bob.areWeWaitingOnRails}
              >
                Make invoice
              </Button>
            </Group>
          ) : (
            /* display amount + QR + Exit button after invoice is ready */
            <>
              {Number.isFinite(amount) && (
                <Stack gap={4} align="center" mt="md">
                  <Text size="lg" fw={700}>
                    {Number(amount).toLocaleString()} sats
                  </Text>
                  <Text size="sm" c="dimmed">
                    {(Number(amount) / 100_000_000).toFixed(8)} BTC
                  </Text>
                  <Text size="sm" c="dimmed">
                    {formatFiat({
                      satoshis: Number(amount),
                      currency: bob.currencyCode,
                      currencySnapshot: bob.workspace.exchangeRates.data,
                    })}
                  </Text>
                </Stack>
              )}

              <UserCreatedInvoiceQR bob={bob} />

              <Group justify="flex-end" mt="md">
                <Button variant="outline" onClick={handleClose}>
                  Exit
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Paper>
    </Modal>
  );
});

export default SetReceiveAmount;
